import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { authAPI, takeAuthNotice } from '../services/api';
import { QRCodeSVG } from 'qrcode.react';
import { Radio, Lock, Mail, Shield, Key, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

// What the API said, if it said anything usable: its `detail` (a string, or FastAPI's
// array of validation objects), else a `message` field.
const apiMessage = (err) => {
  const data = err?.response?.data;
  const detail = data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map(d => (typeof d === 'string' ? d : d?.msg)).filter(Boolean);
    if (msgs.length) return msgs.join('. ');
  }
  if (typeof data?.message === 'string' && data.message) return data.message;
  return null;
};

// Message for a failed call.
//
// A 5xx is the server failing, not the credentials — and a generic server string
// ("An unexpected error occurred") shown on its own is indistinguishable from the SPA
// being broken. Attribute it: say it is server-side, give the status, and keep the
// server's own words alongside so the failing call is still identifiable.
//
// A request that never got a response (network, DNS, CORS, blocked) is named as such
// rather than reported as a rejected password. A client-side throw carries its own
// user-facing copy and is passed through unchanged.
const errorText = (err, fallback) => {
  const status = err?.response?.status;
  const said = apiMessage(err);

  if (!err?.response) {
    if (err?.isAxiosError) {
      const why = err.code || err.message;
      return `Could not reach the sign-in service${why ? ` (${why})` : ''}. Check your connection, then try again — if it persists, contact an administrator.`;
    }
    if (err?.message) return err.message;
    return fallback;
  }

  if (status >= 500) {
    return `The sign-in service failed with a server error (HTTP ${status}${said ? `: ${said}` : ''}). This is a fault on the server, not your password. Please contact an administrator.`;
  }

  if (said) return said;
  return fallback;
};

// /auth/mfa/setup is not uniform about what it calls the QR image, the otpauth URI and the
// shared secret, and it may wrap them in a `data` envelope. Read every plausible key at
// both levels so the enrollment panel always has something to show.
const QR_KEYS = ['qr_code_base64', 'qr_code', 'qr_png_base64', 'qr_image_base64'];
const URI_KEYS = ['provisioning_uri', 'provisioning_url', 'otpauth_uri', 'otpauth_url', 'uri'];
const SECRET_KEYS = ['secret', 'secret_key', 'totp_secret', 'base32'];

const pick = (payload, keys) => {
  const levels = [payload, payload?.data].filter(p => p && typeof p === 'object');
  for (const level of levels) {
    for (const key of keys) {
      if (level[key]) return level[key];
    }
  }
  return undefined;
};

// When only the otpauth:// URI comes back, the shared secret is inside it — pull it out so
// manual entry still works for anyone who cannot scan the code.
const secretFromUri = (uri) => {
  const match = /[?&]secret=([^&]+)/i.exec(String(uri || ''));
  return match ? decodeURIComponent(match[1]) : undefined;
};

const readEnrollment = (payload) => {
  const qr = pick(payload, QR_KEYS);
  const uri = pick(payload, URI_KEYS);
  const secret = pick(payload, SECRET_KEYS) || secretFromUri(uri);
  const qrSrc = qr
    ? (String(qr).startsWith('data:') ? String(qr) : `data:image/png;base64,${qr}`)
    : null;
  return { qrSrc, uri, secret, usable: !!(qrSrc || uri || secret) };
};

function Alert({ children }) {
  return (
    <div role="alert" style={{
      display: 'flex', gap: 10, alignItems: 'flex-start',
      padding: '10px 12px', borderRadius: 8,
      fontSize: 13, lineHeight: 1.45,
      background: 'rgba(255,68,68,0.10)',
      border: '1px solid rgba(255,68,68,0.35)',
      color: '#ffb4b4',
    }}>
      <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{children}</span>
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  // 'credentials' → 'mfa' (already enrolled) or 'enroll' (enrollment required) → 'mfa'
  const [step, setStep] = useState('credentials');
  const [loading, setLoading] = useState(false);
  // Copy stays on the form. A toast alone is not enough: it expires, and anything that
  // remounts the page takes it with it, which is how sign-in came to fail in silence.
  const [error, setError] = useState(null);
  // Enrollment-only token, kept in memory for the two enrollment calls. Never stored.
  const [enrollToken, setEnrollToken] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [enrollCode, setEnrollCode] = useState('');
  const [justEnrolled, setJustEnrolled] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // A dead session redirected here — say so rather than showing a bare form.
  useEffect(() => {
    const notice = takeAuthNotice();
    if (notice) setError(notice);
  }, []);

  const fail = (message) => {
    setError(message);
    toast.error(message);
  };

  const backToCredentials = (message = null) => {
    setStep('credentials');
    setEnrollToken(null);
    setEnrollment(null);
    setEnrollCode('');
    setMfaCode('');
    setJustEnrolled(false);
    setError(message);
  };

  // Enrollment required: fetch the QR / secret using the enrollment token as Bearer.
  const beginEnrollment = async (token) => {
    if (!token) {
      backToCredentials('The two-factor setup token is missing. Sign in again to restart setup.');
      return;
    }
    setEnrollToken(token);
    setEnrollment(null);
    setEnrollCode('');
    setError(null);
    setStep('enroll');
    setLoading(true);
    try {
      const res = await authAPI.mfaSetup(token);
      setEnrollment(res.data);
    } catch (err) {
      const status = err?.response?.status;
      // The enrollment token lasts about ten minutes. Once it is gone there is nothing to
      // retry against, so return to the credentials form — but with copy explaining why.
      if (status === 401 || status === 403) {
        backToCredentials('Your two-factor setup link expired. Sign in again to restart setup.');
        toast.error('Two-factor setup link expired');
        return;
      }
      // Anything else keeps the enrollment step up with a Retry, rather than dumping the
      // user back to a blank credentials form.
      fail(status === 404
        ? 'Two-factor setup is unavailable on the server right now. Please contact an administrator.'
        : errorText(err, 'Could not start two-factor setup. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await login(email, password, step === 'mfa' ? mfaCode : undefined);
      if (result.mfa_enrollment_required) {
        toast('Two-factor setup is required for this account', { icon: '🔐' });
        await beginEnrollment(result.mfa_enrollment_token);
        return;
      }
      if (result.requires_mfa) {
        // Being asked again while already on the code step means the code was rejected.
        if (step === 'mfa') {
          setMfaCode('');
          setError('That code was not accepted. Enter a fresh 6-digit code from your authenticator app.');
          return;
        }
        setStep('mfa');
        setJustEnrolled(false);
        toast('Enter your 2FA code', { icon: '🔐' });
        return;
      }
      toast.success('Welcome back');
      navigate('/');
    } catch (err) {
      fail(errorText(err, 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  };

  // Verify does not issue tokens — on success we ask for a fresh code and log in again so
  // a real session lands.
  const handleVerifyEnrollment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await authAPI.mfaVerify(enrollToken, enrollCode);
      toast.success('MFA enabled successfully');
      setEnrollToken(null);
      setEnrollment(null);
      setEnrollCode('');
      setMfaCode('');
      setJustEnrolled(true);
      setStep('mfa');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        backToCredentials('Your two-factor setup link expired before the code was verified. Sign in again to restart setup.');
        toast.error('Two-factor setup link expired');
        return;
      }
      setEnrollCode('');
      fail(errorText(err, 'That code was not accepted. Enter the current 6-digit code and try again.'));
    } finally {
      setLoading(false);
    }
  };

  const view = enrollment ? readEnrollment(enrollment) : null;
  const enrollReady = !!(view && view.usable);

  const subtitle = {
    credentials: 'Sign in to your admin account',
    mfa: justEnrolled
      ? 'Enter a fresh code from your authenticator to finish signing in'
      : 'Enter your two-factor authentication code',
    enroll: 'Set up two-factor authentication to continue',
  }[step];

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--bg-base)',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Background glow */}
      <div style={{
        position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)',
        width: 600, height: 600, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(108,99,255,0.08) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{ width: '100%', maxWidth: 400, padding: '0 20px', position: 'relative', zIndex: 1 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            width: 56, height: 56, background: 'var(--accent)',
            borderRadius: 14, display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 16px',
            boxShadow: '0 0 40px var(--accent-glow)',
          }}>
            <Radio size={28} color="white" />
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 6 }}>InStore Platform</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{subtitle}</p>
        </div>

        <div className="card" style={{ padding: 32 }}>
          {step === 'enroll' ? (
            <form onSubmit={handleVerifyEnrollment} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Shield size={18} color="var(--accent)" />
                <h3 style={{ fontSize: 15, fontWeight: 700 }}>Two-factor setup required</h3>
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                Scan this QR code with your authenticator app, then enter the 6-digit code it
                shows. This setup link expires in 10 minutes.
              </p>

              {error && <Alert>{error}</Alert>}

              {!enrollReady ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '24px 0', color: 'var(--text-secondary)', fontSize: 13 }}>
                  {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className="spinner" style={{ width: 16, height: 16 }} /> Preparing enrollment...
                    </div>
                  ) : (
                    <>
                      {enrollment && !enrollReady && (
                        <span style={{ textAlign: 'center' }}>
                          The setup response came back with no QR code or key to show.
                        </span>
                      )}
                      <button type="button" className="btn btn-secondary" disabled={!enrollToken}
                        onClick={() => beginEnrollment(enrollToken)}>
                        Retry setup
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <>
                  {view.qrSrc ? (
                    <img
                      src={view.qrSrc}
                      alt="MFA QR code"
                      style={{ width: 200, height: 200, alignSelf: 'center', borderRadius: 10, border: '1px solid var(--border)', background: '#fff' }}
                    />
                  ) : view.uri ? (
                    <div style={{ alignSelf: 'center', padding: 10, background: '#fff', borderRadius: 10, border: '1px solid var(--border)' }}>
                      <QRCodeSVG value={view.uri} size={180} />
                    </div>
                  ) : null}
                  {view.secret && (
                    <div>
                      <label className="form-label">Or enter this key manually</label>
                      <div className="mono" style={{
                        padding: '10px 12px', background: 'var(--bg-elevated)',
                        border: '1px solid var(--border)', borderRadius: 8,
                        fontSize: 13, wordBreak: 'break-all', color: 'var(--text-primary)',
                      }}>
                        {view.secret}
                      </div>
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Verification code</label>
                    <div style={{ position: 'relative' }}>
                      <Key size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                      <input
                        type="text" className="form-input mono"
                        style={{ paddingLeft: 38, letterSpacing: '0.3em', fontSize: 20, textAlign: 'center' }}
                        value={enrollCode} onChange={e => setEnrollCode(e.target.value)}
                        placeholder="000000" maxLength={6} required autoFocus
                      />
                    </div>
                  </div>
                </>
              )}

              <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '12px' }} disabled={loading || !enrollReady}>
                {loading ? <><div className="spinner" style={{ width: 16, height: 16 }} /> Verifying...</> : 'Verify & Enable MFA'}
              </button>
              <button type="button" className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center' }} onClick={() => backToCredentials()}>
                ← Back to login
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {error && <Alert>{error}</Alert>}

              {step !== 'mfa' ? (
                <>
                  <div className="form-group">
                    <label className="form-label">Email address</label>
                    <div style={{ position: 'relative' }}>
                      <Mail size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                      <input
                        type="email" className="form-input" style={{ paddingLeft: 38 }}
                        value={email} onChange={e => setEmail(e.target.value)}
                        placeholder="admin@example.com" required autoFocus
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <div style={{ position: 'relative' }}>
                      <Lock size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                      <input
                        type="password" className="form-input" style={{ paddingLeft: 38 }}
                        value={password} onChange={e => setPassword(e.target.value)}
                        placeholder="••••••••" required
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {justEnrolled && (
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      Two-factor authentication is enabled. Enter a new code from your
                      authenticator app to finish signing in.
                    </p>
                  )}
                  <div className="form-group">
                    <label className="form-label">Two-Factor Code</label>
                    <div style={{ position: 'relative' }}>
                      <Shield size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                      <input
                        type="text" className="form-input mono" style={{ paddingLeft: 38, letterSpacing: '0.3em', fontSize: 20, textAlign: 'center' }}
                        value={mfaCode} onChange={e => setMfaCode(e.target.value)}
                        placeholder="000000" maxLength={6} required autoFocus
                      />
                    </div>
                  </div>
                </>
              )}

              <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '12px' }} disabled={loading}>
                {loading ? <><div className="spinner" style={{ width: 16, height: 16 }} /> Signing in...</> : 'Sign In'}
              </button>

              {step === 'mfa' && (
                <button type="button" className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => backToCredentials()}>
                  ← Back to login
                </button>
              )}
            </form>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: 'var(--text-muted)' }}>
          InStore Media Platform v1.0 · All rights reserved
        </p>
      </div>
    </div>
  );
}
